import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { InstrumentsCustomizationControllerV3ApiFactory } from "@eu.mdotm.api/sphere";
import type { PropagationState } from "./state";

const { newQuery, newAction } = repositoryHelperFactory("instrumentsPropagation");

const customizationApi = () => getApiGen(InstrumentsCustomizationControllerV3ApiFactory);

/** The propagation endpoints of `MDOT-24644`. */
export const propagationRepository = {
	/**
	 * The current propagation state. Required on mount and after any SSE gap: a write response
	 * only exists if *this* session made an edit, and SSE only reports transitions after subscribe.
	 *
	 * A query, not an action: `useQuery` covers the mount read and the tab-focus reconcile for free
	 * (via `refetchOnWindowFocus`), and its `refetch` covers the imperative re-reads (SSE reopen, the
	 * `409` race). It is *not* a reactive source of truth — the page keeps that in local state fed by
	 * five sources and merged by revision ({@link applyPropagationState}); the query's result is just
	 * folded through that same guard. `staleTime` stays at the repository default of `0` so every
	 * mount/focus/refetch hits the network — this state must never be served from cache.
	 */
	getPropagationState: newQuery(
		"getPropagationState",
		async (): Promise<PropagationState> => (await axiosExtract(customizationApi().getPropagationState())).propagation,
		{ refetchOnWindowFocus: true },
	),

	/**
	 * Flush the debounce and propagate immediately. Rejects with a `409` when a propagation is
	 * already running (a normal race, not an error) and a `429` when flushed again inside the backend's
	 * minimum interval. Authorization-gated server-side; {@link canPropagateNow} mirrors the check.
	 */
	propagateNow: newAction(
		async (): Promise<PropagationState> => (await axiosExtract(customizationApi().flushPropagation())).propagation,
	),
} as const;
